import { scoreOutOf100, scoreOutOf100Label } from "@/lib/conditions/score-display";
import { scoreBandLabel } from "@/lib/conditions/score-colour";
import { baseLocalDateISO } from "./bases";

export type CompanionStoredForecastDay = {
  date: string;
  score: number | null;
  sportScores?: Record<string, number | null | undefined> | null;
};

export type CompanionWeekRailPlace = {
  slug: string | null;
  name: string;
  href: string;
  score: number | null;
  verdict: string | null;
  bestWindow: string | null;
  preferredSport?: string | null;
  locationType?: string | null;
  storedForecast?: CompanionStoredForecastDay[] | null;
};

export type CompanionWeekRailTone = "strong" | "useful" | "weak" | "unknown";

export type CompanionWeekRailDay = {
  id: string;
  dayLabel: string;
  dateLabel: string;
  placeSlug: string | null;
  placeName: string | null;
  placeHref: string;
  conditionLabel: string;
  conditionDetail: string;
  conditionTone: CompanionWeekRailTone;
  score: number | null;
  /** Same score on the 0-100 display scale, or null when there is nothing to ring. */
  displayScore: number | null;
  status: "scored" | "unknown" | "today-only";
};

export type CompanionWeekRail = {
  days: CompanionWeekRailDay[];
  summary: string;
  bestDayLabel: string | null;
  todayOnly: boolean;
};

export function buildCompanionWeekRail(
  places: CompanionWeekRailPlace[],
  options: { now?: Date; dayCount?: number; timeZone?: string | null } = {},
): CompanionWeekRail {
  const now = options.now ?? new Date();
  const dayCount = options.dayCount ?? 7;
  const todayOnly = places.length > 0 && places.every(isSailingOnlyPlace);
  const timeZone = options.timeZone ?? "UTC";
  const startDateISO = baseLocalDateISO(timeZone, now);

  const days = Array.from({ length: dayCount }, (_, index) =>
    index === 0
      ? buildTodayRailDay(places, startDateISO)
      : buildFutureRailDay(places, startDateISO, index, todayOnly),
  );

  const bestScoredDay = days
    .filter((day) => day.status === "scored" && day.score !== null)
    .sort((left, right) => (right.score ?? -1) - (left.score ?? -1))[0] ?? null;

  if (todayOnly) {
    return {
      days,
      summary: "Today only for sailing bases.",
      bestDayLabel: bestScoredDay?.dayLabel ?? null,
      todayOnly: true,
    };
  }

  if (!bestScoredDay) {
    return {
      days,
      summary: "No stored forecast for the week yet.",
      bestDayLabel: null,
      todayOnly: false,
    };
  }

  return {
    days,
    summary: `Best day: ${bestScoredDay.dayLabel}.`,
    bestDayLabel: bestScoredDay.dayLabel,
    todayOnly: false,
  };
}

function buildTodayRailDay(
  places: CompanionWeekRailPlace[],
  startDateISO: string,
): CompanionWeekRailDay {
  const day = scaffoldRailDay(startDateISO, 0);
  const fallbackHref = "/conditions";
  const scoredPlaces = places.filter((place) => typeof place.score === "number");
  const bestPlace = scoredPlaces.sort((left, right) => (right.score ?? -1) - (left.score ?? -1))[0]
    ?? places[0]
    ?? null;

  if (!bestPlace) {
    return {
      ...day,
      placeHref: fallbackHref,
      conditionLabel: "No data",
      conditionDetail: "Save a place to build your week.",
      conditionTone: "unknown",
      score: null,
      displayScore: null,
      status: "unknown",
    };
  }

  if (bestPlace.score === null) {
    return {
      ...day,
      placeSlug: bestPlace.slug,
      placeName: bestPlace.name,
      placeHref: bestPlace.href,
      conditionLabel: "No data",
      conditionDetail: bestPlace.verdict ?? "No fresh score.",
      conditionTone: "unknown",
      score: null,
      displayScore: null,
      status: "unknown",
    };
  }

  return {
    ...day,
    placeSlug: bestPlace.slug,
    placeName: bestPlace.name,
    placeHref: bestPlace.href,
    conditionLabel: scoreOutOf100Label(bestPlace.score),
    conditionDetail: buildTodayDetail(bestPlace),
    conditionTone: railTone(bestPlace.score),
    score: bestPlace.score,
    displayScore: scoreOutOf100(bestPlace.score),
    status: "scored",
  };
}

function buildFutureRailDay(
  places: CompanionWeekRailPlace[],
  startDateISO: string,
  dayOffset: number,
  todayOnly: boolean,
): CompanionWeekRailDay {
  const day = scaffoldRailDay(addCalendarDays(startDateISO, dayOffset), dayOffset);

  if (todayOnly) {
    return {
      ...day,
      placeHref: "/conditions",
      conditionLabel: "Today only",
      conditionDetail: "Sailing venues only carry today's signal.",
      conditionTone: "unknown",
      score: null,
      displayScore: null,
      status: "today-only",
    };
  }

  let best:
    | {
        place: CompanionWeekRailPlace;
        score: number;
      }
    | null = null;

  const dateISO = day.id;

  for (const place of places) {
    if (isSailingOnlyPlace(place)) continue;
    const forecastDay = findForecastDay(place.storedForecast ?? null, dateISO);
    if (!forecastDay) continue;
    const forecastScore = selectForecastScore(place, forecastDay);
    if (forecastScore === null) continue;
    if (!best || forecastScore > best.score) {
      best = { place, score: forecastScore };
    }
  }

  if (!best) {
    return {
      ...day,
      placeHref: "/conditions",
      conditionLabel: "No data",
      conditionDetail: "No stored forecast.",
      conditionTone: "unknown",
      score: null,
      displayScore: null,
      status: "unknown",
    };
  }

  return {
    ...day,
    placeSlug: best.place.slug,
    placeName: best.place.name,
    placeHref: best.place.href,
    conditionLabel: scoreOutOf100Label(best.score),
    conditionDetail: `${scoreBandLabel(best.score) ?? "No"} forecast for ${best.place.name}.`,
    conditionTone: railTone(best.score),
    score: best.score,
    displayScore: scoreOutOf100(best.score),
    status: "scored",
  };
}

function scaffoldRailDay(dateISO: string, dayOffset: number): Omit<
  CompanionWeekRailDay,
  | "placeHref"
  | "conditionLabel"
  | "conditionDetail"
  | "conditionTone"
  | "score"
  | "displayScore"
  | "status"
> {
  const date = parseCalendarDate(dateISO);
  return {
    id: dateISO,
    dayLabel: relativeDayLabel(dayOffset, date),
    dateLabel: formatWeekDate(date),
    placeSlug: null,
    placeName: null,
  };
}

function buildTodayDetail(place: CompanionWeekRailPlace): string {
  const verdict = place.verdict ?? "No fresh score.";
  return place.bestWindow ? `${verdict}. Best window ${place.bestWindow}.` : verdict;
}

function findForecastDay(
  forecast: CompanionStoredForecastDay[] | null,
  dateISO: string,
): CompanionStoredForecastDay | null {
  if (!forecast || forecast.length === 0) return null;
  return forecast.find((day) => day.date === dateISO) ?? null;
}

function selectForecastScore(
  place: CompanionWeekRailPlace,
  forecastDay: CompanionStoredForecastDay,
): number | null {
  const preferredSport = normalisePreferredSport(place.preferredSport, place.locationType);
  const locationType = normaliseLocationType(place.locationType);
  const mergedScores = mergedForecastScores(locationType, forecastDay);

  if (preferredSport && mergedScores.has(preferredSport)) {
    return mergedScores.get(preferredSport) ?? null;
  }

  if (preferredSport === "swim") {
    return mergedScores.get("swimming") ?? null;
  }

  if (preferredSport === "bodyboarding" && locationType === "surf") {
    return mergedScores.get("surf") ?? null;
  }

  if (preferredSport === "piste" || preferredSport === "surf") {
    return mergedScores.get(preferredSport) ?? null;
  }

  return null;
}

function mergedForecastScores(
  locationType: string | null,
  forecastDay: CompanionStoredForecastDay,
): Map<string, number | null> {
  const merged = new Map<string, number | null>();
  if (locationType === "ski") merged.set("piste", numberOrNull(forecastDay.score));
  if (locationType === "surf") merged.set("surf", numberOrNull(forecastDay.score));

  for (const [key, value] of Object.entries(forecastDay.sportScores ?? {})) {
    merged.set(normaliseLocationType(key) ?? key, numberOrNull(value));
  }

  return merged;
}

function isSailingOnlyPlace(place: CompanionWeekRailPlace): boolean {
  const locationType = normaliseLocationType(place.locationType);
  const preferredSport = normalisePreferredSport(place.preferredSport, place.locationType);
  return locationType === "sailing" || preferredSport === "sailing-club" || preferredSport === "sailing-hp";
}

function railTone(score: number | null): CompanionWeekRailTone {
  if (score === null) return "unknown";
  if (score >= 7.5) return "strong";
  if (score >= 5.5) return "useful";
  return "weak";
}

function relativeDayLabel(dayOffset: number, date: Date): string {
  if (dayOffset === 0) return "Today";
  if (dayOffset === 1) return "Tomorrow";
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    timeZone: "UTC",
  }).format(date);
}

function formatWeekDate(date: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(date);
}

function addCalendarDays(dateISO: string, days: number): string {
  const next = parseCalendarDate(dateISO);
  next.setUTCDate(next.getUTCDate() + days);
  return next.toISOString().slice(0, 10);
}

function parseCalendarDate(dateISO: string): Date {
  return new Date(`${dateISO}T00:00:00.000Z`);
}

function normalisePreferredSport(
  preferredSport: string | null | undefined,
  locationType: string | null | undefined,
): string | null {
  const key = normaliseLocationType(preferredSport ?? locationType);
  if (!key) return null;
  if (key === "ski" || key === "snowboard") return "piste";
  if (key === "swimming") return "swim";
  if (key === "sailing") return "sailing-club";
  return key;
}

function normaliseLocationType(value: string | null | undefined): string | null {
  const key = value?.toLowerCase().trim().replace(/_/g, "-");
  return key || null;
}

function numberOrNull(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}
